import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Pagination from '@mui/material/Pagination';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useAllVendors } from '../../resources/useAllVendors';
import useDebounce from '../../resources/hooks/UseDebounce';
import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import { usePayerData } from '../../resources/hooks/usePayerDataHook';
import { URLS } from '../../resources/constants';
import { getBranch } from '../../store/user/selectors';
import PaymentFormModal, {
  EMPTY_PAYMENT,
  PAYMENT_METHODS,
  PaymentFormValues,
} from './PaymentFormModal';
import VendorFormModal, {
  EMPTY_VENDOR,
  VendorFormValues,
} from '../Tax1099Vendors/VendorFormModal';

/**
 * 1099 vendor payments: list, record, edit, soft delete.
 *
 * Every request is scoped by the selected branch, and the scene waits for one
 * before fetching -- gl-api requires branch_id and would 422 without it.
 *
 * The header total is the API's `reportable_total`: gross across every matching
 * reportable row, all pages. A page-local sum would be the wrong number to
 * compare against a filing threshold.
 */

const PAGE_SIZE_OPTIONS = [10, 20, 50];
// PaymentSource.GL_AUTO_MATCH in gl-api: assigned by exact check-payee match
// rather than confirmed by a reviewer. The other sources are not distinguished
// here, so the filter is a two-state "auto or not", not a source picker.
const SOURCE_AUTO_MATCH = 5;

const EMPTY_VALUE = '--';


interface PaymentRow {
  id: number;
  vendor_id: number;
  vendor_code?: string | null;
  vendor_display_name?: string | null;
  payment_date: string;
  gross_amount: string;
  federal_tax_withheld: string;
  state_tax_withheld: string;
  net_amount: string;
  payment_method: number;
  is_1099_reportable: boolean;
  reportable_form_type?: string | null;
  reportable_box?: string | null;
  reference_number?: string | null;
  source?: number | null;
}

interface VendorOption {
  id: number;
  vendor_code: string;
  display_name: string;
  default_form_type?: string | null;
  default_box?: string | null;
}

const currentYear = new Date().getFullYear();
// Paid basis, so the useful range is this year and the years already closed.
const TAX_YEARS = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

const money = (value?: string | null) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : EMPTY_VALUE;
};

const Tax1099Payments: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('Tax1099Payments');
  const { branchId, businessName } = usePayerData();

  const {
    fetchPaymentList,
    fetchPaymentDetails,
    createPayment,
    updatePayment,
    deletePayment,
    createVendor,
    fetchNextVendorCode,
  } = useTax1099Api();

  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [reportableTotal, setReportableTotal] = useState('0');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showQuickVendorModal, setShowQuickVendorModal] = useState(false);
  const [quickVendorInitial, setQuickVendorInitial] = useState<VendorFormValues>(EMPTY_VENDOR);
  const [quickVendorSaving, setQuickVendorSaving] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [vendorSearchInput, setVendorSearchInput] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const vendorDropdownRef = useRef<HTMLDivElement>(null);
  const [taxYear, setTaxYear] = useState('');
  const [reportableFilter, setReportableFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [showForm, setShowForm] = useState(false);
  const [formInitial, setFormInitial] = useState<PaymentFormValues>(EMPTY_PAYMENT);

  const methodLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    PAYMENT_METHODS.forEach((method) => {
      labels[method.value] = t(method.key);
    });
    return labels;
  }, [t]);

  // useDebounce here is an effect hook -- it runs the callback after the delay
  // rather than returning a debounced value.
  useDebounce(
    () => {
      const next = searchInput.trim();
      setPage(1);
      setSearch((previous) => (previous === next ? previous : next));
    },
    [searchInput],
    400,
  );

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / pageSize) : 1),
    [total, pageSize],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target as Node)) {
        setShowVendorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Shared with the other 1099 screens, and invalidated by the mutation
  // wrappers in useTax1099Api -- a quick-added vendor shows up without a
  // manual refetch here.
  const { vendors, reload: loadVendors } = useAllVendors({ branchId });

  const filteredVendors = useMemo(() => {
    const query = vendorSearchInput.trim().toLowerCase();
    if (!query) {
      return vendors.slice(0, 20);
    }
    return vendors
      .filter((v) =>
        (v.display_name && v.display_name.toLowerCase().includes(query)) ||
        (v.vendor_code && v.vendor_code.toLowerCase().includes(query))
      )
      .slice(0, 25);
  }, [vendors, vendorSearchInput]);


  const loadPayments = useCallback(() => {
    if (!branchId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const params: Record<string, any> = {
      branch_id: branchId,
      page,
      page_size: pageSize,
    };
    if (search) {
      params.search = search;
    }
    if (vendorFilter) {
      params.vendor_id = vendorFilter;
    }
    if (taxYear) {
      params.tax_year = taxYear;
    }
    if (reportableFilter !== '') {
      params.is_1099_reportable = reportableFilter === '1';
    }
    if (sourceFilter === 'auto') {
      params.source = SOURCE_AUTO_MATCH;
    }

    fetchPaymentList(
      params,
      (message: string, response: any) => {
        // ResponseModel wraps the payload, so the axios body is
        // { success, message, data: { count, results } } -- hence data.data.
        const data = response?.data?.data ?? {};
        setRows(data.results ?? []);
        setTotal(data.count ?? 0);
        setReportableTotal(String(data.reportable_total ?? 0));
        setLoading(false);
      },
      (message: string) => {
        setLoading(false);
        toast.error(message || t('errors.load_failed'));
      },
    );
    // eslint-disable-next-line
  }, [branchId, page, pageSize, search, vendorFilter, taxYear, reportableFilter, sourceFilter]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1);
  }, [vendorFilter, taxYear, reportableFilter, sourceFilter, pageSize]);

  const openCreate = () => {
    setFormInitial({ ...EMPTY_PAYMENT, vendor_id: vendorFilter });
    setShowForm(true);
  };

  const openEdit = (paymentId: number) => {
    if (!branchId) {
      return;
    }
    fetchPaymentDetails(
      { payment_id: paymentId, branch_id: branchId },
      (message: string, response: any) => {
        const payment = response?.data?.data ?? {};
        setFormInitial({
          id: payment.id,
          vendor_id: String(payment.vendor_id ?? ''),
          payment_date: payment.payment_date ?? '',
          gross_amount: String(payment.gross_amount ?? ''),
          federal_tax_withheld: String(payment.federal_tax_withheld ?? ''),
          state_tax_withheld: String(payment.state_tax_withheld ?? ''),
          payment_method: String(payment.payment_method ?? '1'),
          // The stored flag may be an override, so it is shown explicitly
          // rather than as "derive" -- reopening must not silently re-derive it.
          is_1099_reportable: payment.is_1099_reportable ? '1' : '0',
          reportable_form_type: payment.reportable_form_type ?? '',
          reportable_box:
            payment.reportable_form_type === '1099-NEC' && !payment.reportable_box
              ? 'NEC-1'
              : (payment.reportable_box ?? ''),
          reference_number: payment.reference_number ?? '',
          withholding_state_code: payment.withholding_state_code ?? '',
          memo: payment.memo ?? '',
        });
        setShowForm(true);
      },
      (message: string) => toast.error(message || t('errors.load_failed')),
    );
  };

  const handleOpenQuickAddVendor = (queryName: string, draft: PaymentFormValues) => {
    setFormInitial({ ...draft });
    const cleanName = (queryName || '').trim();
    let maxNum = 0;
    vendors.forEach((v) => {
      if (v.vendor_code && v.vendor_code.startsWith('VND-')) {
        const num = parseInt(v.vendor_code.slice(4), 10);
        if (!isNaN(num) && num > maxNum) maxNum = num;
      }
    });
    const fallbackCode = `VND-${String(maxNum + 1).padStart(5, '0')}`;

    setQuickVendorInitial({
      ...EMPTY_VENDOR,
      display_name: cleanName,
      vendor_code: fallbackCode,
      default_form_type: '1099-NEC',
      default_box: 'NEC-1',
      is_1099_eligible: '1',
      source: 'manual',
    });
    setShowQuickVendorModal(true);

    if (branchId) {
      fetchNextVendorCode(
        { branch_id: branchId },
        (_message: string, response: any) => {
          const nextCode = response?.data?.data?.vendor_code;
          if (nextCode) {
            setQuickVendorInitial((prev: any) => ({ ...prev, vendor_code: nextCode }));
          }
        }
      );
    }
  };

  const handleQuickVendorSubmit = (values: VendorFormValues) => {
    if (!branchId) return;
    setQuickVendorSaving(true);
    const payload: Record<string, any> = {
      ...values,
      branch_id: branchId,
    };
    createVendor(
      payload,
      (message: string, response: any) => {
        setQuickVendorSaving(false);
        setShowQuickVendorModal(false);
        const newVendor = response?.data?.data ?? {};
        toast.success(message || t('messages.vendor_created', 'Vendor created successfully'));
        loadVendors();
        if (newVendor.id) {
          setFormInitial((prev) => ({
            ...prev,
            vendor_id: String(newVendor.id),
            reportable_form_type: newVendor.default_form_type || '1099-NEC',
            reportable_box: newVendor.default_box || 'NEC-1',
          }));
        }
      },
      (message: string) => {
        setQuickVendorSaving(false);
        toast.error(message || t('errors.save_failed'));
      },
    );
  };

  /** Empty strings mean "not set" in the form but must not reach the API as "". */
  const toPayload = (values: PaymentFormValues) => {
    const payload: Record<string, any> = {
      branch_id: branchId,
      payment_date: values.payment_date,
      gross_amount: values.gross_amount,
      payment_method: Number(values.payment_method),
      // Zero is a real value, so these are always sent rather than pruned.
      federal_tax_withheld: values.federal_tax_withheld || '0',
      state_tax_withheld: values.state_tax_withheld || '0',
    };

    if (!values.id) {
      payload.vendor_id = Number(values.vendor_id);
      if (values.post_to_register) {
        payload.post_to_register = true;
        payload.expense_master_chart_of_account_id = Number(values.expense_master_chart_of_account_id);
        payload.funding_master_chart_of_account_id = Number(values.funding_master_chart_of_account_id);
      }
    }

    const optionalText: Array<keyof PaymentFormValues> = [
      'reportable_form_type', 'reportable_box', 'reference_number',
      'withholding_state_code', 'memo',
    ];
    optionalText.forEach((field) => {
      const value = String(values[field] ?? '').trim();
      if (value) {
        payload[field] = value;
      }
    });

    // Omitted entirely when blank, so gl-api derives it from payment_method.
    if (values.is_1099_reportable !== '') {
      payload.is_1099_reportable = values.is_1099_reportable === '1';
    }
    return payload;
  };

  const handleSubmit = (values: PaymentFormValues) => {
    setSaving(true);
    const payload = toPayload(values);

    const onSuccess = (message: string) => {
      setSaving(false);
      setShowForm(false);
      toast.success(message || t('messages.saved'));
      loadPayments();
    };
    const onError = (message: string) => {
      setSaving(false);
      toast.error(message || t('errors.save_failed'));
    };

    if (values.id) {
      updatePayment({ ...payload, payment_id: values.id }, onSuccess, onError);
    } else {
      createPayment({ ...payload, vendor_id: Number(values.vendor_id) }, onSuccess, onError);
    }
  };

  const handleDelete = (row: PaymentRow) => {
    if (!branchId) {
      return;
    }
    if (!window.confirm(t('confirm.delete', {
      amount: money(row.gross_amount),
      vendor: row.vendor_display_name || EMPTY_VALUE,
    }))) {
      return;
    }
    deletePayment(
      { payment_id: row.id, branch_id: branchId },
      (message: string) => {
        toast.success(message || t('messages.deleted'));
        loadPayments();
      },
      (message: string) => toast.error(message || t('errors.delete_failed')),
    );
  };

  if (!branchId) {
    return (
      <div className="p-4">
        <h4>{t('page.title')}</h4>
        <p className="text-muted">{t('messages.select_branch')}</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0" style={{ color: '#0d2c55', fontWeight: 800 }}>
            {t('page.title', '1099 Payment Ledger')}
          </h4>
          <small className="text-muted">{t('page.subtitle')}</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            style={{
              borderColor: '#14509e',
              color: '#14509e',
              backgroundColor: '#fff',
              fontWeight: 700,
            }}
            onClick={() => navigate(URLS.VENDOR_CENTER)}
          >
            👥 {t('actions.vendor_management', 'Vendor Management')}
          </Button>
          <Button
            style={{ backgroundColor: '#14509e', borderColor: '#14509e', color: '#fff', fontWeight: 700 }}
            onClick={openCreate}
            disabled={vendors.length === 0}
          >
            + {t('actions.record_payment', 'Record Payment')}
          </Button>
        </div>
      </div>

      {vendors.length === 0 && (
        <div className="alert alert-warning py-2">{t('messages.no_vendors_yet')}</div>
      )}

      <div className="alert alert-info py-2">
        {t('messages.reportable_total', {
          year: taxYear || t('filter.all_years'),
          amount: money(reportableTotal),
        })}
      </div>

      <div className="row mb-3">
        <div className="col-md-3 mb-2">
          <Form.Control
            type="search"
            placeholder={t('filter.search_placeholder')}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
        <div className="col-md-2 mb-2" ref={vendorDropdownRef} style={{ position: 'relative' }}>
          <div className="position-relative">
            <Form.Control
              type="search"
              placeholder={t('filter.vendor_search_placeholder', 'Search vendor by name or code...')}
              value={vendorSearchInput}
              onChange={(event) => {
                const val = event.target.value;
                setVendorSearchInput(val);
                setShowVendorDropdown(true);
                if (!val) {
                  setVendorFilter('');
                }
              }}
              onFocus={() => setShowVendorDropdown(true)}
            />
            {vendorFilter && (
              <button
                type="button"
                className="btn btn-sm btn-link text-muted position-absolute end-0 top-50 translate-middle-y text-decoration-none"
                style={{ padding: '0 10px', fontSize: '14px', zIndex: 5 }}
                onClick={() => {
                  setVendorFilter('');
                  setVendorSearchInput('');
                  setShowVendorDropdown(false);
                }}
                title={t('filter.clear_vendor', 'Clear vendor filter')}
              >
                ✕
              </button>
            )}
          </div>
          {showVendorDropdown && (
            <div
              className="dropdown-menu show w-100 shadow border"
              style={{ maxHeight: '250px', overflowY: 'auto', zIndex: 1050, position: 'absolute', top: '100%', left: 0 }}
            >
              <button
                type="button"
                className={`dropdown-item small ${!vendorFilter ? 'active fw-bold' : ''}`}
                onClick={() => {
                  setVendorFilter('');
                  setVendorSearchInput('');
                  setShowVendorDropdown(false);
                }}
              >
                {t('filter.all_vendors', 'All vendors')}
              </button>
              {filteredVendors.length === 0 ? (
                <div className="px-3 py-2 small text-muted">No vendors matching "{vendorSearchInput}"</div>
              ) : (
                filteredVendors.map((vendor) => (
                  <button
                    key={vendor.id}
                    type="button"
                    className={`dropdown-item small ${String(vendor.id) === vendorFilter ? 'active fw-bold' : ''}`}
                    onClick={() => {
                      setVendorFilter(String(vendor.id));
                      setVendorSearchInput(`${vendor.vendor_code} — ${vendor.display_name}`);
                      setShowVendorDropdown(false);
                    }}
                  >
                    <span className="fw-bold">{vendor.vendor_code}</span> — {vendor.display_name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <div className="col-md-2 mb-2">
          <Form.Select value={taxYear} onChange={(event) => setTaxYear(event.target.value)}>
            <option value="">{t('filter.all_years')}</option>
            {TAX_YEARS.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-2 mb-2">
          <Form.Select
            value={reportableFilter}
            onChange={(event) => setReportableFilter(event.target.value)}
          >
            <option value="">{t('filter.all_reportability')}</option>
            <option value="1">{t('filter.reportable_only')}</option>
            <option value="0">{t('filter.excluded_only')}</option>
          </Form.Select>
        </div>
        <div className="col-md-2 mb-2">
          <Form.Select
            value={sourceFilter}
            onChange={(event) => setSourceFilter(event.target.value)}
          >
            <option value="">{t('filter.all_sources')}</option>
            <option value="auto">{t('filter.auto_matched_only')}</option>
          </Form.Select>
        </div>
        <div className="col-md-1 mb-2">
          {(searchInput || vendorFilter || taxYear || reportableFilter || sourceFilter) && (
            <Button
              variant="outline-secondary"
              className="w-100"
              onClick={() => {
                setSearchInput('');
                setVendorFilter('');
                setVendorSearchInput('');
                setTaxYear('');
                setReportableFilter('');
                setSourceFilter('');
              }}
              title={t('filter.clear', 'Clear filters')}
            >
              ✕
            </Button>
          )}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle" style={{ fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr>
              <th>{t('table.headers.payment_date')}</th>
              <th>{t('table.headers.vendor')}</th>
              <th className="text-end">{t('table.headers.gross_amount')}</th>
              <th className="text-end">{t('table.headers.withheld')}</th>
              <th className="text-end">{t('table.headers.net_amount')}</th>
              {/* ps-4: Method is left-aligned right after the right-aligned
                  amounts, so without a gutter Net's figures butt into it. */}
              <th className="ps-4">{t('table.headers.payment_method')}</th>
              {/* Searchable, so it has to be visible -- filtering on a column
                  the grid never shows is unverifiable from the screen. */}
              <th className="ps-4">{t('table.headers.reference_number')}</th>
              <th>{t('table.headers.box')}</th>
              <th>{t('table.headers.reportable')}</th>
              <th className="text-end">{t('table.headers.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="text-center text-muted py-4">
                  <div className="spinner-border spinner-border-sm me-2 text-primary" role="status" />
                  {t('messages.loading')}
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center text-muted py-4">
                  {t('messages.no_payments')}
                </td>
              </tr>
            )}

            {!loading && rows.map((row) => (
              <tr key={row.id}>
                <td>{row.payment_date}</td>
                <td>
                  {row.vendor_display_name || EMPTY_VALUE}
                  {row.source === SOURCE_AUTO_MATCH && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      {t('table.headers.auto_badge')}
                    </Badge>
                  )}
                  <div className="text-muted small">
                    <code>{row.vendor_code}</code>
                  </div>
                </td>
                <td className="text-end">{money(row.gross_amount)}</td>
                <td className="text-end">
                  {money(
                    String(
                      Number(row.federal_tax_withheld ?? 0)
                      + Number(row.state_tax_withheld ?? 0),
                    ),
                  )}
                </td>
                <td className="text-end">{money(row.net_amount)}</td>
                <td className="ps-4">{methodLabels[String(row.payment_method)] || EMPTY_VALUE}</td>
                <td className="ps-4">{row.reference_number || EMPTY_VALUE}</td>
                <td>{row.reportable_box ? row.reportable_box.replace('_', '-') : EMPTY_VALUE}</td>
                <td>
                  {row.is_1099_reportable
                    ? <Badge bg="success">{t('reportable.yes')}</Badge>
                    : <Badge bg="secondary">{t('reportable.no')}</Badge>}
                </td>
                <td className="text-end">
                  <Button variant="link" size="sm" onClick={() => openEdit(row.id)}>
                    {t('actions.edit')}
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-danger"
                    onClick={() => handleDelete(row)}
                  >
                    {t('actions.delete')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">{t('pagination.rows_per_page')}</span>
          <Form.Select
            size="sm"
            style={{ width: 90 }}
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Form.Select>
          <span className="text-muted small">
            {t('pagination.total', { count: total })}
          </span>
        </div>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_event, value) => setPage(value)}
          size="small"
        />
      </div>

      <PaymentFormModal
        show={showForm && !showQuickVendorModal}
        saving={saving}
        initial={formInitial}
        vendors={vendors}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmit}
        onOpenQuickAddVendor={handleOpenQuickAddVendor}
      />

      <VendorFormModal
        show={showQuickVendorModal}
        saving={quickVendorSaving}
        initial={quickVendorInitial}
        onClose={() => setShowQuickVendorModal(false)}
        onSubmit={handleQuickVendorSubmit}
      />
    </div>
  );
};

export default Tax1099Payments;
